import React, { useState, useEffect } from 'react'
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { State } from '../../types/state'
import { Package } from '../../enums/package.enum'
import { isMinimumPackage } from '../../utils/package-validator'
import { useRequests } from '../../requests/useRequests'
import { Tabs, Tab, Tooltip, IconButton } from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalFloristIcon from '@mui/icons-material/LocalFlorist'
import PersonIcon from '@mui/icons-material/Person'
import { RefreshActions } from '../../state/refresh'
import { useExcelExport } from '../../hooks/useExcelExport'
import { IColumnType } from '../../types/column'
import { ExportColumnsModal } from './ProjectDetails/components/ExportColumnsModal'
import { ShareModal } from './ProjectDetails/components/ShareModal'
import { ProjectWithComments } from '../../types/project'
import { SideMenu } from '../Header/SiteMenu'
import TabContent from './ProjectDetails/components/TabContent'
import AppNavBar from '../Header/AppNavBar'
import ItemImageModal from './ProjectDetails/components/ItemImageModal'
import ProjectDetailsIcons from './ProjectDetails/ProjectDetailsIcons'
import ExportPdfModal from './ProjectDetails/components/ExportPdfModal'
import ExportMoodboardModal from './ProjectDetails/components/ExportMoodboardModal'

const defaultColumns: IColumnType = {
  number: true,
  stage: true,
  name: true,
  type: true,
  price: true,
  quantity: true,
  unit: true,
  value: true,
  url: true,
  image: true,
  dimensions: true,
  description: true,
  status: true,
  deliveryCost: true,
  discountPercentage: true,
  marginPercentage: true,
  vatPercentage: true,
  category: true,
  catalogNumber: true,
  supplier: true
}

const ProjectDetails = () => {
  const requestMaker = useRequests()
  const { id } = useParams()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'details')
  const [project, setProject] = useState<ProjectWithComments | null>(null)

  const { package: userPackage, packageExpiredAt, username } = useSelector((state: State) => state.user)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { exportToExcel: exportToExcelHook } = useExcelExport()

  // Modale i stany do eksportu/udostępniania
  const [showExportColumns, setShowExportColumns] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedShareColumns, setSelectedShareColumns] = useState<IColumnType>(project?.selectedColumns|| defaultColumns)
  const [emailToShare, setEmailToShare] = useState('')
  const [sharingEmails, setSharingEmails] = useState<string[]>([])

  // Modale i stany do obsługi ItemImageModal
  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedItemForImage, setSelectedItemForImage] = useState<{ id: string, imageUrl?: string } | null>(null)

  // Modale i stany do eksportu PDF i moodboard
  const [showExportPdfModal, setShowExportPdfModal] = useState(false)
  const [showExportMoodboardModal, setShowExportMoodboardModal] = useState(false)

  useEffect(() => {
    fetchProject()
  }, [id, location])

  useEffect(() => {
    const interval = setInterval(() => {
      if (id && activeTab === 'products') {
        fetchProject()
      }
    }, 10000) // 10 seconds

    return () => clearInterval(interval)
  }, [id, activeTab])

  useEffect(() => {
    setSelectedShareColumns(project?.selectedColumns|| defaultColumns)
  }, [project?.selectedColumns])

  const fetchProject = async () => {
    if (!id) return
    const projectData = await requestMaker.getProject(id)
    if (projectData) {
      if (JSON.stringify(projectData.data) !== JSON.stringify(project)) {
        setProject(projectData.data as ProjectWithComments)
        setSharingEmails(projectData.data.sharedWithEmails || [])
      }
    }
  }

  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue)
    setSearchParams({ tab: newValue })
  }

  const handleProjectUpdate = (updatedProject: ProjectWithComments) => {
    setProject(updatedProject)
  }

  // Akcje z ProjectActions
  const handleCopyShareUrl = () => {
    if (project?.shareUrl) {
      const fullShareUrl = `${window.location.origin}/projects/shared/${project.shareUrl}`
      navigator.clipboard.writeText(fullShareUrl)
        .then(() => {
          dispatch(RefreshActions.setToastMessage({ message: 'Link został skopiowany do schowka' }))
        })
        .catch(() => {
          dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się skopiować linku do schowka' }))
        })
    }
  }

  const handleSendShareLinkViaEmail = async (email: string) => {
    if (!project?.shareUrl || !project.id) return
    const fullShareUrl = `${window.location.origin}/projects/shared/${project.shareUrl}`
    const success = await requestMaker.sendProjectInvitations(project.id, { emails: [email], shareUrl: fullShareUrl })
    if (success) {
      setSharingEmails([...sharingEmails, email])
      setEmailToShare('')
      dispatch(RefreshActions.setToastMessage({ message: 'Zaproszenie zostało wysłane' }))
    }
  }

  const handleUpdateShareColumns = async () => {
    if (!project?.id) return
    try {
      await requestMaker.shareProject(project.id, { selectedColumns: selectedShareColumns })
      const updated = await requestMaker.getProject(project.id)
      if (updated && updated.data) setProject(updated.data as ProjectWithComments)
      dispatch(RefreshActions.setToastMessage({ message: 'Kolumny udostępniania zostały zaktualizowane' }))
    } catch {
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się zaktualizować kolumn udostępniania' }))
    }
  }

  const handleShareProject = async () => {
    try {
      const response = await requestMaker.shareProject(project!.id, { selectedColumns: selectedShareColumns })
      if (response) {
        const updated = await requestMaker.getProject(project!.id)
        if (updated && updated.data) {
          setProject(updated.data as ProjectWithComments)
          setShowShareModal(false)
          setTimeout(() => setShowShareModal(true), 0)
        }
      }
    } catch {
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się udostępnić projektu' }))
    }
  }

  const handleUnshareProject = async () => {
    try {
      const success = await requestMaker.unshareProject(project!.id)
      if (success) {
        setProject({ ...project!, isShared: false, shareUrl: null })
      }
    } catch {
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się wyłączyć udostępniania projektu' }))
    }
  }

  const handleExportToExcel = () => {
    if (isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt)) {
      setShowExportColumns(true)
    } else {
      exportToExcelHook(
        project!,
        username,
        userPackage || 'free',
        (packageExpiredAt instanceof Date)
          ? packageExpiredAt.toISOString()
          : (typeof packageExpiredAt === 'string' ? packageExpiredAt : null),
        selectedShareColumns,
        () => {
          dispatch(RefreshActions.setToastMessage({ message: 'Plik Excel został wygenerowany' }))
          setShowExportColumns(false)
        },
        (message) => {
          dispatch(RefreshActions.setToastMessage({ message }))
        }
      )
    }
  }

  const handleExportToTasks = async () => {
    if (project == null) return
    try {
      const boardId = await requestMaker.createTaskBoard({
        title: `Zadania z projektu: ${project.name}`,
        description: `Tablica zadań wygenerowana automatycznie na podstawie projektu "${project.name}"`
      })
      if (boardId) {
        const goToTaskBoard = window.confirm('Czy chcesz przejść do utworzonej tablicy zadań?')
        if (goToTaskBoard) navigate(`/tasks/${boardId}`)
      }
    } catch {
      dispatch(RefreshActions.setToastMessage({ message: 'Nie udało się utworzyć tablicy zadań' }))
    }
  }

  const handleSelectAllColumns = () => setSelectedShareColumns({ ...defaultColumns })
  const handleDeselectAllColumns = () => {
    const allColumns: IColumnType = Object.keys(defaultColumns).reduce((acc, key) => {
      acc[key as keyof IColumnType] = false
      return acc
    }, {} as IColumnType)
    setSelectedShareColumns(allColumns)
  }

  const handleOpenImageModal = (itemId: string, imageUrl?: string) => {
    setSelectedItemForImage({ id: itemId, imageUrl })
    setShowImageModal(true)
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setSelectedItemForImage(null)
  }

  const handleImageSave = async () => {
    handleCloseImageModal()
    const updatedProject = await requestMaker.getProject(project!.id)
    if (updatedProject) {
      setProject(updatedProject.data as ProjectWithComments)
    }
  }

  const handleExportPdf = async (options: any) => {
    if (project == null) return
    try {
      const blob = await requestMaker.exportProjectPdf(project.id, options)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${project.name || 'projekt'}-lista.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
      setShowExportPdfModal(false)
      dispatch(RefreshActions.setToastMessage({ message: 'PDF został wygenerowany' }))
    } catch (e) {
      dispatch(RefreshActions.setToastMessage({ message: 'Błąd generowania PDF' }))
    }
  }

  const handleExportMoodboard = async (options: any) => {
    if (project == null) return
    try {
      const blob = await requestMaker.exportProjectMoodboard(project.id, options)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${project.name || 'projekt'}-moodboard.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
      setShowExportMoodboardModal(false)
      dispatch(RefreshActions.setToastMessage({ message: 'Moodboard PDF został wygenerowany' }))
    } catch (e) {
      dispatch(RefreshActions.setToastMessage({ message: 'Błąd generowania PDF' }))
    }
  }

  if (project == null) {
    return (
      <div className='loading-container text-center py-50'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-20'>Ładowanie projektu...</p>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText={project?.name || 'Szczegóły projektu'}
        rightIcons={[
          <ProjectDetailsIcons
            key='project-details-icons'
            activeTab={activeTab}
            onTabChange={handleTabChange}
            project={project}
            onShareProject={handleShareProject}
            onUnshareProject={handleUnshareProject}
            onOpenShareModal={() => setShowShareModal(true)}
            onExportToExcel={handleExportToExcel}
            onExportToPdf={() => setShowExportPdfModal(true)}
            onExportToMoodboard={() => setShowExportMoodboardModal(true)}
            onExportToTasks={handleExportToTasks}
          />
        ]}
      />
      <ExportColumnsModal
        show={showExportColumns}
        onClose={() => setShowExportColumns(false)}
        selectedExportColumns={selectedShareColumns}
        setSelectedExportColumns={setSelectedShareColumns}
        handleSelectAllColumns={handleSelectAllColumns}
        handleDeselectAllColumns={handleDeselectAllColumns}
        onExport={async () => await exportToExcelHook(
          project,
          username,
          userPackage || 'free',
          (packageExpiredAt instanceof Date)
            ? packageExpiredAt.toISOString()
            : (typeof packageExpiredAt === 'string' ? packageExpiredAt : null),
          selectedShareColumns,
          () => {
            dispatch(RefreshActions.setToastMessage({ message: 'Plik Excel został wygenerowany' }))
            setShowExportColumns(false)
          },
          (message) => {
            dispatch(RefreshActions.setToastMessage({ message }))
          }
        )}
      />
      <ShareModal
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        project={project}
        selectedShareColumns={selectedShareColumns}
        setSelectedShareColumns={setSelectedShareColumns}
        handleSelectAllShareColumns={handleSelectAllColumns}
        handleDeselectAllShareColumns={handleDeselectAllColumns}
        copyShareUrl={handleCopyShareUrl}
        emailToShare={emailToShare}
        setEmailToShare={setEmailToShare}
        sendShareLinkViaEmail={handleSendShareLinkViaEmail}
        sharingEmails={sharingEmails}
        handleUpdateShareColumns={handleUpdateShareColumns}
      />
      {(selectedItemForImage != null) && (
        <ItemImageModal
          show={showImageModal}
          onClose={handleCloseImageModal}
          onSave={handleImageSave}
          itemId={selectedItemForImage.id}
          projectId={project.id}
          currentImageUrl={selectedItemForImage.imageUrl}
        />
      )}
      <ExportPdfModal
        show={showExportPdfModal}
        onClose={() => setShowExportPdfModal(false)}
        project={project}
        onExport={handleExportPdf}
      />
      <ExportMoodboardModal
        show={showExportMoodboardModal}
        onClose={() => setShowExportMoodboardModal(false)}
        project={project}
        onExport={handleExportMoodboard}
      />
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <SideMenu />
        <section className='project-details pt-40 pb-40' style={{ flex: 1, overflow: 'auto', background: '#f8f9fa', padding: '0 24px' }}>
          <div className='container'>
            <div className='tab-content' style={{ padding: '20px 0' }}>
              <TabContent
                activeTab={activeTab}
                project={project}
                onProjectUpdate={handleProjectUpdate}
                onOpenImageModal={handleOpenImageModal}
              />
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ProjectDetails
